package ui.report.device

import androidx.compose.animation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import common.frontend.api.IoReport
import io.Repository
import kotlinx.coroutines.launch
import kotlinx.datetime.*
import kotlinx.datetime.format.MonthNames
import kotlinx.datetime.format.char
import kotlinx.serialization.json.*
import srializable.JSON_INSTANCE
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.printStackTraceInDebug
import ui.report.device.ReportDeviceSystemScope.Companion.DATE_FORMAT
import ui.widget.ErrUI
import ui.widget.LoadingUI
import kotlin.time.Duration


data class ReportDeviceArgs(var deviceId: Long = -1L)

abstract class ReportDeviceSystemScope(val deviceId: Long) {

    abstract val vm: ReportDeviceSystemVM
    abstract val action: (ReportDeviceSystemVM.Action) -> Unit

    companion object {
        val DATE_FORMAT = LocalDateTime.Format {
            year()
            char(' ')
            monthName(MonthNames.ENGLISH_ABBREVIATED)
            char(' ')
            day()
            char(' ')
            char(' ')
            hour()
            char(':')
            minute()
        }

        @Composable
        fun rememberScope(): ReportDeviceSystemScope {
            val args = LocalNavigationArgs.current.rememberArgs(ReportDeviceArgs::class)
            val nav = LocalNavigation.current
            val vm = nav.viewModel(ReportDeviceSystemVM::class, args.deviceId.toString())
            val action = ReportDeviceSystemVM.Action.rememberAction(vm)

            return remember(args) {
                object : ReportDeviceSystemScope(args.deviceId) {
                    override val vm: ReportDeviceSystemVM
                        get() = vm
                    override val action: (ReportDeviceSystemVM.Action) -> Unit
                        get() = action

                }.apply {
                    vm.init(this)
                }
            }
        }

    }
}

@VM
class ReportDeviceSystemVM : NavigationViewModel() {

    private lateinit var scopeUi: ReportDeviceSystemScope

    var state by mutableDataState<List<IoReport.ListingDeviceSystem.Item>>()
        private set

    var canLoadMore by mutableStateOf(false)
        private set

    sealed interface Action {
        data object LoadMore : Action
        companion object {

            @Composable
            fun rememberAction(vm: ReportDeviceSystemVM): (Action) -> Unit {
                return remember {
                    { act ->
                        when (act) {
                            LoadMore -> vm.loadMore()
                        }
                    }
                }
            }
        }
    }

    private fun loadMore() {
        canLoadMore = false
        scope.launch {
            state.entry?.also { oldItems ->
                runCatching {
                    val items = Repository.api.report.listingDeviceSystem(scopeUi.deviceId, 0).items
                    state = DataState.Result(oldItems + items)
                    canLoadMore = items.size == 100
                }.onFailure {
                    it.printStackTraceInDebug()
                    state = DataState.Error(it.message!!, throwable = it)
                }
            }

        }
    }

    override fun onRelease() {
        state = DataState.Idle()
    }

    fun init(scopeUi: ReportDeviceSystemScope) {
        this.scopeUi = scopeUi
        this.scopeUi = scopeUi
        if (state !is DataState.Result) {
            state = DataState.Loading()
        }
        scope.launch {
            runCatching {
                val items = Repository.api.report.listingDeviceSystem(scopeUi.deviceId, 0).items
                state = DataState.Result(items)
                canLoadMore = items.size == 100
            }.onFailure {
                it.printStackTraceInDebug()
                state = DataState.Error(it.message!!, throwable = it)
            }
        }
    }
}


@Composable
@ComposableNavNode(ReportDeviceArgs::class)
fun ReportDeviceSystemUI() {

    with(ReportDeviceSystemScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@Composable
fun ReportDeviceSystemScope.Content(nnvl: List<IoReport.ListingDeviceSystem.Item>) {
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        verticalArrangement = Arrangement.spacedBy(8.dp),
        contentPadding = PaddingValues(16.dp)
    ) {
        items(nnvl) {
            Item(it)
        }
        if (vm.canLoadMore) {
            item {
                Box(Modifier.fillMaxWidth()) {
                    Button(
                        onClick = { action(ReportDeviceSystemVM.Action.LoadMore) },
                        modifier = Modifier.align(Alignment.Center)
                            .padding(16.dp)
                    ) {
                        Text("LOAD MORE")
                    }
                }
            }
        }
    }
}

@Composable
fun ReportDeviceSystemScope.Item(item: IoReport.ListingDeviceSystem.Item) {
    var isExpanded by remember(item.id) { mutableStateOf(false) }
    Card(
        onClick = { isExpanded = !isExpanded },
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(Modifier.fillMaxWidth().padding(8.dp)) {
            val name = remember {
                item.utc.toInstant(TimeZone.UTC).toLocalDateTime(TimeZone.currentSystemDefault())
                    .format(DATE_FORMAT)
            }

            Text(name)

            AnimatedVisibility(
                isExpanded,
                enter = expandIn() + fadeIn(),
                exit = shrinkOut() + fadeOut(),
                modifier = Modifier.fillMaxWidth()
            ) {
                JsonViewer(
                    element = JSON_INSTANCE.encodeToJsonElement(item.data).jsonObject,
                    level = 2,
                    modifier = Modifier.fillMaxWidth()
                        .padding(top = 16.dp)
                        .padding(8.dp)
                )
            }
        }
    }
}

@Composable
private fun JsonViewer(element: JsonElement, level: Int, modifier: Modifier = Modifier) {
    Card(
        modifier = modifier,
        elevation = CardDefaults.elevatedCardElevation(level.dp)
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(4.dp),
            modifier = Modifier.padding(8.dp)
        ) {
            if (element is JsonObject) {
                element.entries.forEachIndexed { _, entry ->
                    Row {
                        when (val value = entry.value) {
                            is JsonArray -> {
                                Text(entry.key, modifier = Modifier.weight(1F))
                                Column(
                                    modifier = Modifier.weight(3F),
                                    verticalArrangement = Arrangement.spacedBy(4.dp),
                                ) {
                                    value.forEach {
                                        JsonViewer(it, level + 4)
                                    }
                                }
                            }

                            is JsonObject -> {
                                Text(entry.key, modifier = Modifier.weight(1F))
                                if (value.isEmpty()) {
                                    Text(value.toString(), Modifier.weight(3F))
                                } else {
                                    JsonViewer(value, level + 4, Modifier.weight(3F))
                                }
                            }

                            else -> {
                                runCatching {
                                    Text(entry.key, modifier = Modifier.weight(1F))
                                    value.jsonPrimitive.toString().replace("\"", "").also {
                                        if (it.startsWith("PT")) {
                                            Text(Duration.parse(it).toString(), modifier = Modifier.weight(3F))
                                        } else {
                                            Text(it, modifier = Modifier.weight(3F))
                                        }
                                    }
                                }.onFailure {
                                    it.printStackTraceInDebug()
                                    Text(value.toString(), modifier = Modifier.weight(3F))
                                }

                            }
                        }
                    }
                }
            }
        }
    }
}
